import React, { useCallback, useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { Spinner } from '@toqio/toqio-web-components';
import { useBridge } from '../../bridge/useBridge';
import { useFlowReady } from '../../bridge/useFlowReady';
import { EmptyState, PageContainer, PrimaryButton } from '../../shared/components';
import { CardSection } from './components/CardSection';
import { RecentActivity } from './components/RecentActivity';
import { MOBILE, tok } from './components/styles';
import { useDashboardData } from './useDashboardData';

/**
 * card-dashboard — the supplemental-card holder's read-only home screen.
 *
 * A single data-driven screen (no fields, no wizard): it fetches the whole
 * dashboard on entry, paints cards (grid on desktop / carousel on mobile, a
 * pure CSS @media split at 697px) and grouped recent activity, then silently
 * re-fetches every 30 s. Interactions are hand-offs to the host
 * (`navigate:native`). Bypasses `StepWizard` (LESSONS § "skip StepWizard on
 * flows with independent entry points") and is `readiness: 'manual'` — it
 * signals readiness once the first load settles so its internal Spinner is
 * never revealed.
 *
 * See `flows/card-dashboard/PRD.md` and `API_CONTRACT.md`.
 */
const FLOW_ID = 'card-dashboard';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  /* 59px between the card section and Recent Activity matches the legacy
     inter-panel spacing; mobile keeps the tighter 16px. */
  gap: 59px;
  /* Owns its padding (PageContainer is $bleed). Top is 10px to match the
     legacy page's content-top offset; sides mirror the lum-web gutter — 24px on
     desktop, 0 at >=1440 so the content reaches the full 1140px like the native
     container (else the cards row is 1092, not 1140); full-bleed on mobile so
     the 100svw carousel reaches the viewport edges. */
  padding: 10px 24px 24px;
  background: ${({ theme }) => tok(theme, 'general_background', '#f2f3f3')};

  @media (min-width: 1440px) {
    padding: 10px 0 24px;
  }

  @media (max-width: ${MOBILE}) {
    /* Sides full-bleed for the carousel; 16px below the closing list separator
       to match the legacy. */
    padding: 0 0 16px;
    gap: 16px;
  }
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 56px 24px;
  min-height: 50vh;
`;

/* card-dashboard only: hide the iframe's own scrollbar on mobile (mirrors the
   legacy `scrollbar-width: none`), so the styled bar doesn't show over the
   full-bleed carousel. Scoped here — mounted with the flow — rather than in the
   app-wide GlobalStyle, so other flows keep their scrollbar. */
const HideScrollbarMobile = createGlobalStyle`
  @media (max-width: ${MOBILE}) {
    html {
      scrollbar-width: none;
    }
    ::-webkit-scrollbar {
      width: 0;
      height: 0;
    }
  }
`;

// `.lum-web-container` only centres its 1140px cap at XXL (≥1440px); below that
// it left-aligns. PageContainer centres at every width, so mirror the native
// left-align here (scoped to this flow) to keep the side gutter identical.
const Bleed = styled(PageContainer)`
  @media (max-width: 1439px) {
    margin-left: 0;
    margin-right: auto;
  }
`;

export const CardDashboardFlow: React.FC = () => {
  const bridge = useBridge();
  const { signalReady } = useFlowReady();
  const locale = bridge.locale ?? 'en';

  const { phase, data, reload } = useDashboardData(locale);
  const [selectedIdx, setSelectedIdx] = useState(0);

  // Emit step:change once on mount (bypassing StepWizard — it is its own
  // standalone screen).
  useEffect(() => {
    bridge.emit({
      type: 'step:change',
      flowId: FLOW_ID,
      stepId: 'overview',
      stepIndex: 0,
      totalSteps: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Host back signal → leave the embed.
  useEffect(
    () => bridge.onBackRequest(() => bridge.emit({ type: 'cancel', flowId: FLOW_ID })),
    [bridge],
  );

  // Manual readiness: drop the host overlay once the first load settles
  // (success or error), never revealing the internal Spinner.
  useEffect(() => {
    if (phase === 'loading') return;
    signalReady();
  }, [phase, signalReady]);

  // Card / action / transaction hand-off — emit the app-relative path verbatim
  // (PRD §8 / O-7); the host resolves it on web and RN.
  const onNavigate = useCallback(
    (route: string) => bridge.emit({ type: 'navigate:native', route }),
    [bridge],
  );

  if (phase === 'loading') {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }

  if (phase === 'error') {
    return (
      <Centered>
        <EmptyState
          title={t`Error`}
          description={t`We could not get your card information at this time. Please try again later.`}
        />
        <PrimaryButton onClick={reload}>
          <Trans>Try again</Trans>
        </PrimaryButton>
      </Centered>
    );
  }

  const cards = data?.cardList ?? [];
  const groups = data?.transactionGroups ?? [];

  // No cards: both regions collapse to the no-cards empty state (PRD §9).
  if (cards.length === 0) {
    return (
      <Centered>
        <EmptyState
          title={t`No cards yet`}
          description={t`You do not have a card yet. Please ask the main account holder to issue a card for you.`}
        />
      </Centered>
    );
  }

  const safeIdx = Math.min(selectedIdx, cards.length - 1);

  return (
    <Bleed $bleed>
      <HideScrollbarMobile />
      <Root>
        <CardSection
          firstName={data?.user.firstName ?? ''}
          cards={cards}
          selectedIdx={safeIdx}
          onSelect={setSelectedIdx}
          onNavigate={onNavigate}
        />
        <RecentActivity groups={groups} locale={locale} onNavigate={onNavigate} />
      </Root>
    </Bleed>
  );
};

export default CardDashboardFlow;
